import React, { useState } from 'react';
import { Screen } from '../App';
import { User, isTrialExpired } from '../storage';
import * as authApi from '../authApi';

interface Props { navigate: (s: Screen) => void; user: User | null; refreshUser: () => Promise<any>; checkoutReturn?: boolean; }

export default function Paywall({ navigate, user, refreshUser, checkoutReturn }: Props) {
  // Reached voluntarily (still on trial) vs. because the trial ran out — the
  // heading and the way back differ.
  const trialOver = user ? isTrialExpired(user) : true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(!!checkoutReturn);

  const handleSubscribe = async (plan: 'monthly' | 'annual') => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const url = await authApi.createCheckout(plan);
      window.location.href = url; // hand off to Stripe's hosted checkout page
    } catch (e: any) {
      setError(e?.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  // Returning from Stripe checkout: the account is marked paid by the webhook,
  // which may land a moment after the redirect. Poll the server a few times until
  // it shows paid, then go to the dashboard.
  React.useEffect(() => {
    if (!checkoutReturn) return;
    let tries = 0;
    let cancelled = false;
    const tick = async () => {
      tries++;
      const u = await refreshUser();
      if (cancelled) return;
      if (u && u.isPaid) { navigate('dashboard'); return; }
      if (tries >= 6) { setConfirming(false); return; } // ~12s; webhook not through yet
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [checkoutReturn]); // eslint-disable-line

  const logout = async () => { await authApi.logout(); navigate('landing'); };

  if (confirming) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div className="spinner" />
        <p style={{ color: 'var(--teal)', fontWeight: 700, fontSize: 16 }}>Confirming your subscription…</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', maxWidth: 300, lineHeight: 1.6 }}>
          This takes just a moment. Thank you for subscribing!
        </p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '40px 24px' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <span style={{ fontFamily: 'Playfair Display', fontSize: 22, fontWeight: 700, color: 'var(--teal)' }}>Decode My Student</span>
      </div>

      <div className="card" style={{ textAlign: 'center', marginBottom: 20, padding: '32px 24px' }}>
        <p style={{ fontSize: 40, marginBottom: 16 }}>{trialOver ? '⏰' : '💛'}</p>
        {trialOver ? (
          <>
            <h2 style={{ marginBottom: 12 }}>Your 7 days are up — but your work is not gone.</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 8 }}>
              Every student profile, every conversation, every win you logged is saved and waiting for you.
            </p>
            <p style={{ color: 'var(--teal)', fontWeight: 600, fontSize: 15 }}>Subscribe to pick up exactly where you left off.</p>
          </>
        ) : (
          <>
            <h2 style={{ marginBottom: 12 }}>Ready to keep going?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 8 }}>
              You're still on your free trial — no rush. Subscribe whenever you're ready and your access simply continues without interruption.
            </p>
          </>
        )}
      </div>

      {/* Annual plan */}
      <div className="card" style={{ border: '2px solid var(--teal)', marginBottom: 12, position: 'relative', padding: '28px 24px' }}>
        <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--amber)', color: 'white', padding: '3px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
          BEST VALUE
        </div>
        <p style={{ textAlign: 'center', fontWeight: 700, fontSize: 13, color: 'var(--teal)', marginBottom: 4 }}>Annual Plan</p>
        <p style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, marginBottom: 2 }}>$299<span style={{ fontSize: 14, fontWeight: 400 }}>/year</span></p>
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Just $24.92/month, billed annually</p>
        <button onClick={() => handleSubscribe('annual')} className="btn-primary" disabled={loading}>
          {loading ? 'Connecting to payment...' : 'Subscribe Annual — $299/year'}
        </button>
      </div>

      {/* Monthly plan */}
      <div className="card" style={{ marginBottom: 16, padding: '24px' }}>
        <p style={{ textAlign: 'center', fontWeight: 700, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>Monthly Plan</p>
        <p style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, marginBottom: 2 }}>$29<span style={{ fontSize: 14, fontWeight: 400 }}>/mo</span></p>
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Cancel anytime · No contracts</p>
        <button onClick={() => handleSubscribe('monthly')} className="btn-secondary" disabled={loading}>
          {loading ? 'Connecting to payment...' : 'Subscribe Monthly — $29/mo'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, fontSize: 14, lineHeight: 1.6, color: 'var(--charcoal)' }}>
          {error}
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Cancel anytime. No contracts. Your data is always yours.
      </p>
      <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Need a school or district plan? <span style={{ color: 'var(--teal)', fontWeight: 600 }}>decodemystudent@gmail.com</span>
      </p>

      {/* Safety net: a teacher who already paid but shows as unpaid (e.g. a webhook
          hiccup) can still reach the portal / cancellation from here. */}
      <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Already subscribed? <span onClick={() => navigate('billing')} style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }}>Manage or cancel your subscription</span>
      </p>

      {trialOver ? (
        <button onClick={logout} style={{ background: 'none', color: 'var(--text-secondary)', fontSize: 13, width: '100%', textDecoration: 'underline', border: 'none', cursor: 'pointer' }}>
          Log out
        </button>
      ) : (
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--text-secondary)', fontSize: 13, width: '100%', textDecoration: 'underline', border: 'none', cursor: 'pointer' }}>
          Maybe later — back to my dashboard
        </button>
      )}
    </div>
  );
}
